/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps } from '@msdyn365-commerce/core';
import { Button } from '@msdyn365-commerce-modules/utilities';
import React, { useCallback } from 'react';

/**
 * Resources for the remove from comparison button component.
 */
export interface IRemoveFromComparisonButtonResources {
    productComparisonRemoveProductFromListAriaLabel: string;
}

/**
 * The configuration of the remove from comparison button component.
 */
export interface IRemoveFromComparisonButtonComponentProps extends IComponentProps {
    className: string;
    itemId: string;

    resources: IRemoveFromComparisonButtonResources;

    onClickHandler(itemId: string): void;
}

/**
 * Represents a button which handles adding to product comparison.
 * @param props - Configuration of the component.
 * @returns React functional component.
 */
const RemoveFromComparisonButtonFunctionalComponent: React.FC<IRemoveFromComparisonButtonComponentProps> = (
    props: IRemoveFromComparisonButtonComponentProps
) => {
    const onClick = useCallback(() => {
        props.onClickHandler(props.itemId);
    }, [props]);

    const { className } = props;

    return <Button className={className} title={props.resources.productComparisonRemoveProductFromListAriaLabel} onClick={onClick} />;
};

/**
 * Represents a button which handles removing the product from the comparison.
 * @param props - Configuration of the component.
 * @returns React functional component.
 */
export const RemoveFromComparisonButtonComponent: React.FunctionComponent<IRemoveFromComparisonButtonComponentProps> = (
    props: IRemoveFromComparisonButtonComponentProps
) => React.createElement(RemoveFromComparisonButtonFunctionalComponent, props);
